import {Button, DialogActions, DialogContent, DialogTitle, Tab, Tabs} from "@mui/material";
import {Dialog2} from "../../../../misc/Dialog2";
import {SmoothActionBar} from "../../../../misc/SmoothActionBar";
import {useTableData} from "../../../../misc/scroller/Table";
import {currentTab, openingsTableName} from "../openings/Openings";
import {Opening} from "../../../../api/Openings";
import {useProject, useProjectId} from "../ProjectName";
import {useIsQuote} from "../../quote/QuoteContainer";
import {currentQuoteTab, quoteOpeningsTableName} from "../../quote/openings/QuoteOpenings";
import {QuoteOpening} from "../../../../api/QuoteOpenings";
import {getOpeningSizeSubmittal, OpeningSizeSubData} from "./SzSubmittalLoader";
import React, {useContext, useEffect, useState} from "react";
import {PrintManagerContext} from "../../../../misc/pdf/PrintManager";
import {useSubmittalPreferences} from "../../../../misc/pdf/useSubmittalPreferences";
import {distinct, first} from "nate-react-api-helpers";
import {ColumnTab} from "../openings/pdf/ColumnTab";
import {
    ExportColumn,
    openingColumns, OpeningSizeSubmittalPrefs,
} from "../openings/MakeSubmittal";
import {api} from "../../../../api/API";
import {getAll} from "../../../logistics/routes/useGetAll";
import {useAlternative} from "../../quote/alternative/Alternative";
import {useCurrentContractor} from "../../quote/CurrentContractor";
import {OpeningsTab, OpeningWithSelected} from "../openings/pdf/OpeningsTab";
import {TabWrapper} from "../TabWrapper";
import {tabGroup} from "../../../logistics/purchasing/PartsToSource";

const defaultColumns = [
    "name",
    "openingType",
    "nominalWidth",
    "nominalHeight",
    "drawingWallLabel",
    "frameSeries",
    "screenElevation",
    "wallType",
    "jambDepth",
    "frameConstruction",
    "frameAnchorProduct",
];

const allowedColumns = [
    "name",
    "openingType",
    "nominalWidth",
    "nominalHeight",
    "drawingWallLabel",
    "frameSeries",
    "screenElevation",
    "wallType",
    "jambDepth",
    "frameConstruction",
    "frameAnchorProduct",
];

const columns = openingColumns.filter(c => allowedColumns.includes(c.key));

export function OpeningSizeSubmittal(props: {
    onDone(): void;
}) {
    const projectId = useProjectId();
    const quote = useIsQuote();
    const openings = useTableData(quote ? quoteOpeningsTableName(projectId, currentQuoteTab) : openingsTableName(projectId, currentTab)) as (Opening|QuoteOpening)[]

    const missingDimensions = openings.filter(o => {
        if(o.drwScreenElevation || o.drwScreenRendering) {
            // the outside size arrives with the screen rendering (user or default)
            return !o.drwScreenRenderingMeta;
        }

        if(o.screenElevation !== "") {
            if(o.screenElevationOutsideHeight === "") return true;
            if(o.screenElevationOutsideWidth === "") return true;
        }

        return false;
    })

    const [selected, setSelected] = useState<ExportColumn[]>(
        defaultColumns
            .map(key => columns.find(c => c.key === key))
            .filter(v => !!v) as ExportColumn[]
    );
    const printCtx = useContext(PrintManagerContext);
    const project = useProject();
    const isQuote = useIsQuote();
    const alternative = useAlternative();
    const [tab, setTab] = useState("columns");
    const [selectedOpenings, setSelectedOpenings] = useState<OpeningWithSelected[]>([]);

    useEffect(() => {
        setSelectedOpenings(openings.map(o => ({
            ...o,
            selected: true,
        })));
    }, [openings]);

    const prefs = useSubmittalPreferences({
        project: projectId,
        kind: "frame-openings",
        onLoad: (list: OpeningSizeSubmittalPrefs) => {
            if ("version" in list && list.version === 1) {
                setSelected(list.columns.map(l => first(columns, c => c.key === l))
                    .filter(v => !!v) as ExportColumn[])
            }
        }
    });

    const {getContractorDetails, CurrentContractorPromptElement} = useCurrentContractor({
        onAutoPromptCanceled: props.onDone
    });

    if(missingDimensions.length > 0) {
        const haveScreen: string[] = [];
        const byScreen = missingDimensions.filter(v => {
            if(haveScreen.includes(v.screenElevation)) return false;
            haveScreen.push(v.screenElevation);
            return true;
        })

        return (
            <Dialog2 open onClose={props.onDone}>
                <DialogTitle>
                    Opening Size Submittal
                </DialogTitle>
                <DialogContent>
                    <div>
                        The following openings are missing dimensions:
                        <ul>
                            {byScreen.map(o => <li key={o.id}>{o.screenElevation}</li>)}
                        </ul>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={props.onDone}>Close</Button>
                </DialogActions>
            </Dialog2>
        )
    }

    if(CurrentContractorPromptElement) {
        return (<>{CurrentContractorPromptElement}</>);
    }

    if(prefs.loading) return null;

    const submit = async () => {
        await prefs.save({
            version: 1,
            columns: selected.map(c => c.key),
        })

        if(!project) return;

        await printCtx.print2(getOpeningSizeSubmittal({
            async getData(): Promise<OpeningSizeSubData> {
                const projectP = api.projects.get(projectId)
                const openingP = isQuote ?
                    getAll(null, offset => api.quoteOpenings.list({project: projectId, alternative, offset})) :
                    getAll(null, offset => api.openings.list({project: projectId, offset}));

                const project = await projectP;
                const {client, clientContact} = await getContractorDetails();
                const selectedIds = selectedOpenings.filter(o => o.selected).map(o => o.id);
                let openings = (await openingP) as (Opening|QuoteOpening)[];
                openings = openings.filter(o => selectedIds.includes(o.id) && !!o.frameSeries && o.frameConstruction);

                const anchors = await api.products.listById({
                    ids: distinct(openings.map(o => o.frameAnchorProduct).filter(v => !!v)) as number[]
                });

                const anchorsWithCatalogs = anchors.filter(a => a.catalogs && a.catalogs.length > 0)

                const hasWallType = selected.some(v => v.key === "drawingWallLabel");

                const wallTypeImageIds = distinct(openings.map(o => o.drawingWallLabelFile).filter(v => !!v)) as number[];
                const wallTypeImages = Promise.all(!hasWallType ? [] : wallTypeImageIds.map(s => api.print.fileAsImage({file: s})));

                const anchorImages = Promise.all(anchorsWithCatalogs
                        .map(v => api.print.fileAsImage({catalogForProduct: v.id}))) as Promise<string[]>;

                return {
                    client: client,
                    clientContact: clientContact,
                    data: openings,
                    sys: project.unitSystem,
                    project: project,
                    columns: selected,
                    wallTypeImages: await wallTypeImages,
                    anchorImages: await anchorImages,
                }
            },
            projectId: projectId,
        }));

        props.onDone();
    };

    return (
        <Dialog2 open maxWidth="lg" fullWidth onClose={props.onDone} onEnterPress={submit}>
            <DialogTitle>Create Opening Size Submittal</DialogTitle>
            <TabWrapper>
                <Tabs className={tabGroup} value={tab} onChange={(e, value) => setTab(value)}>
                    <Tab label="Columns" value="columns" />
                    <Tab label="Openings" value="openings" />
                </Tabs>
            </TabWrapper>
            <DialogContent>
                {tab === "columns" ?
                    <ColumnTab value={selected} onChange={setSelected} sample={openings[0]} columns={columns} /> :
                    <OpeningsTab value={selectedOpenings} onChange={setSelectedOpenings} />}
            </DialogContent>
            <SmoothActionBar
                onCancel={props.onDone}
                onSubmit={submit}
                actionName="Create and Download"
            />
        </Dialog2>
    )
}

